import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Steps, Input, Select, Button, message, Tag, Empty, Alert } from 'antd';
import { EnvironmentOutlined, UserOutlined, CompassOutlined, CheckCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import api from '../../services/api';

const ONBOARDING_SNOOZE_KEY = 'onboarding_snoozed_until';
const SNOOZE_HOURS = 24;
const POSITIONS = ['goleiro', 'zagueiro', 'lateral', 'meio', 'volante', 'atacante', 'linha'];
const LEVELS = ['iniciante', 'intermediario', 'avancado'];

function isSnoozed() {
  const t = parseInt(localStorage.getItem(ONBOARDING_SNOOZE_KEY) || '0', 10);
  return t && Date.now() < t;
}

// Wizard 3 passos que aparece SOMENTE quando faltam dados criticos pro
// matchmaking (lat/lng OU preferred_position). Nao substitui o
// ProfileCompletionCard — este e o onboarding em profundidade.
export default function OnboardingModal() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [cep, setCep] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [position, setPosition] = useState(null);
  const [level, setLevel] = useState(null);
  const [nearbyTeams, setNearbyTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);

  useEffect(() => {
    if (isSnoozed()) return;
    (async () => {
      try {
        const r = await api.get('/community/players/profile');
        const missingGeo = !r.data.latitude || !r.data.longitude;
        const missingPos = !r.data.preferred_position;
        if (missingGeo || missingPos) {
          setPosition(r.data.preferred_position || null);
          setLevel(r.data.skill_level || null);
          setCep(r.data.cep || '');
          setOpen(true);
        }
      } catch { /* silencioso */ }
    })();
  }, []);

  const snooze = () => {
    localStorage.setItem(ONBOARDING_SNOOZE_KEY, String(Date.now() + SNOOZE_HOURS * 3600_000));
    setOpen(false);
  };

  // ViaCEP autofill
  const onCepBlur = useCallback(async () => {
    const clean = cep.replace(/\D/g, '');
    if (clean.length !== 8) return;
    try {
      const r = await axios.get(`https://viacep.com.br/ws/${clean}/json/`);
      if (r.data && !r.data.erro) {
        setAddress(r.data.logradouro || '');
        setCity(r.data.localidade || '');
        setState(r.data.uf || '');
        setNeighborhood(r.data.bairro || '');
      }
    } catch { /* silencioso */ }
  }, [cep]);

  const saveStep1 = async () => {
    if (!cep.replace(/\D/g, '').length) { message.warning('Informa seu CEP'); return; }
    setSaving(true);
    try {
      await api.put('/community/players/profile', {
        cep, address, city, state, neighborhood,
        is_public: true, is_available: true, // preserva flag (default true)
      });
      setStep(1);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const saveStep2 = async () => {
    if (!position) { message.warning('Escolhe uma posição'); return; }
    setSaving(true);
    try {
      await api.put('/community/players/profile', {
        preferred_position: position,
        skill_level: level || 'intermediario',
        is_public: true, is_available: true,
      });
      // Carrega times proximos pro step 3
      setLoadingTeams(true);
      try {
        const r = await api.get('/community/players/nearby-teams?radius=30&limit=5');
        setNearbyTeams(r.data.teams || []);
      } catch { setNearbyTeams([]); }
      setLoadingTeams(false);
      setStep(2);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const applyToTeam = async (team) => {
    try {
      // Tenta pegar proximo jogo aberto
      let gameId = null;
      try {
        const r = await api.get(`/community/games/team/${team.id}/next-open`);
        gameId = r.data?.id || null;
      } catch { /* segue GENERIC */ }
      const body = gameId
        ? { team_id: team.id, message: `Quero jogar no ${team.name || team.game_name}`, game_id: gameId, kind: 'GAME' }
        : { team_id: team.id, message: `Quero conhecer o ${team.name || team.game_name}`, kind: 'GENERIC' };
      await api.post('/community/invites/apply-to-team', body);
      message.success('Bora! Candidatura mandada.');
      setNearbyTeams(ts => ts.map(t => t.id === team.id ? { ...t, applied: true } : t));
    } catch (e) {
      message.error(e?.response?.data?.message || 'Erro ao candidatar');
    }
  };

  const finish = () => {
    localStorage.setItem(ONBOARDING_SNOOZE_KEY, String(Date.now() + 365 * 24 * 3600_000));
    setOpen(false);
    window.location.reload(); // recarrega pra puxar dashboard com dados novos
  };

  return (
    <Modal
      open={open}
      onCancel={snooze}
      title="⚽ Bora achar sua próxima pelada"
      footer={null}
      width={560}
      maskClosable={false}
    >
      <Steps
        current={step}
        size="small"
        style={{ marginBottom: 20 }}
        items={[
          { title: 'Onde', icon: <EnvironmentOutlined /> },
          { title: 'Perfil', icon: <UserOutlined /> },
          { title: 'Times perto', icon: <CompassOutlined /> },
        ]}
      />

      {step === 0 && (
        <div>
          <Alert type="info" showIcon style={{ marginBottom: 16 }}
            message="Só o essencial: teu CEP. Assim a gente já mostra time boleiro perto." />
          <label style={{ fontSize: 12, color: '#888' }}>CEP</label>
          <Input
            placeholder="00000-000"
            value={cep}
            onChange={e => {
              // Mascara automatica 00000-000
              const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
              setCep(digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits);
            }}
            onBlur={onCepBlur}
            maxLength={9}
            inputMode="numeric"
            style={{ marginBottom: 12 }}
          />
          {city && (
            <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
              📍 {neighborhood ? `${neighborhood}, ` : ''}{city}/{state}
            </div>
          )}
          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Button onClick={snooze} type="text">Depois</Button>
            <Button type="primary" onClick={saveStep1} loading={saving}>Continuar</Button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div>
          <label style={{ fontSize: 12, color: '#888' }}>Sua posição</label>
          <Select
            placeholder="Selecione"
            value={position}
            onChange={setPosition}
            style={{ width: '100%', marginBottom: 12 }}
            options={POSITIONS.map(p => ({ value: p, label: p }))}
          />
          <label style={{ fontSize: 12, color: '#888' }}>Nível técnico</label>
          <Select
            placeholder="Escolhe uma"
            value={level}
            onChange={setLevel}
            style={{ width: '100%' }}
            options={LEVELS.map(l => ({ value: l, label: l }))}
          />
          <div style={{ textAlign: 'right', marginTop: 20 }}>
            <Button onClick={() => setStep(0)} type="text">Voltar</Button>
            <Button type="primary" onClick={saveStep2} loading={saving}>Ver times perto</Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          {loadingTeams ? (
            <div style={{ textAlign: 'center', padding: 20 }}>Buscando times...</div>
          ) : nearbyTeams.length === 0 ? (
            <Empty description="Nenhum time por aqui ainda. A gente te avisa quando abrir jogo perto — pode confiar." />
          ) : (
            <>
              <div style={{ marginBottom: 12, fontSize: 13, color: '#666' }}>
                Achamos <b>{nearbyTeams.length}</b> {nearbyTeams.length === 1 ? 'time' : 'times'} perto de ti:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {nearbyTeams.map(t => (
                  <div key={t.id} style={{
                    border: '1px solid #eef1f4', borderRadius: 12, padding: 12,
                    display: 'flex', gap: 12, alignItems: 'center',
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700 }}>{t.name || t.game_name}</div>
                      <div style={{ fontSize: 12, color: '#888' }}>
                        {t.neighborhood ? `${t.neighborhood} · ` : ''}{t.city}
                        {t.distance_km != null && <Tag color="blue" style={{ marginLeft: 6 }}>{t.distance_km.toFixed(1)} km</Tag>}
                      </div>
                      {t.skill_level && <Tag>{t.skill_level}</Tag>}
                    </div>
                    {t.applied ? (
                      <Tag color="green"><CheckCircleOutlined /> Enviado</Tag>
                    ) : (
                      <Button size="small" type="primary" onClick={() => applyToTeam(t)}>Bora!</Button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
          <div style={{ textAlign: 'right', marginTop: 20 }}>
            <Button type="primary" onClick={finish}>Concluir</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
